import React, { useState, useContext, useEffect } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import { UserContext } from "../UserContext";
import { useNavigate } from "react-router-dom";
import Footer from "../Footer";
import AdminNav from "../Navbars/AdminNav";
import TeacherNav from "../Navbars/TeacherNav";
import StudentNav from "../Navbars/StudentNav";
import "./mycalender.css";
import { Tooltip } from "react-tooltip";
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

function MyCalendar() {
  const {
    isAdmin,
    isTeacher,
    isStudent,
    isGuest,
    setUserRole,
    setUserInfo,
    userInfo,
  } = useContext(UserContext);

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calendarDate, setCalendarDate] = useState(new Date());

  // Responsive: show Calendar label for mobile/tablet
  const [showMobileLabel, setShowMobileLabel] = useState(window.innerWidth <= 932);
  useEffect(() => {
    const handleResize = () => setShowMobileLabel(window.innerWidth <= 932);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      await fetch(`${(process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_LOCAL_SERVER_API)}`, {
        credentials: "include",
      }).then((response) => {
        response
          .json()
          .then((userInfo) => {
            setUserInfo(userInfo);
            if (userInfo?.usertype) {
              const uType = userInfo?.usertype;
              setUserRole(uType);
            }
            const uname = userInfo?.username;
            const avatar = userInfo?.avatar;
          })
          .catch((err) => console.log("Error in useeffect:", err))
          .finally(() => setLoading(false));
      }).catch((err) => {
        setLoading(false);
      });
    };
    fetchData();
  }, [isAdmin, isTeacher, isStudent, isGuest]);

  useEffect(() => {
    if (!userInfo || userInfo.valid === false) {
      return;
    }
    // Check for different possible user ID fields
    const userId = userInfo.User_id || userInfo.id || userInfo.user_id || userInfo.userId;
    if (!userId) {
      return;
    }
    const fetchData = async () => {
      try {
        let apiUrl = '';
        if (isStudent) {
          apiUrl = `${(process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_LOCAL_SERVER_API)}/api/calenderview/getstudentcalenderview`;
        } else if (isTeacher) {
          apiUrl = `${(process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_LOCAL_SERVER_API)}/api/calenderview/getteachercalenderview`;
        } else if (isAdmin) {
          return;
        } else {
          return;
        }
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ User_id: userId }),
        });
        if (!response.ok) {
          return;
        }
        const data = await response.json();
        const transformedEvents = data.map((item) => {
          // Calculate the date for this event based on ClassDay and ClassTime
          const daysOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
          const dayIndex = daysOfWeek.indexOf(item.ClassDay);
          const today = new Date();
          const todayIndex = today.getDay();
          const diff = (dayIndex - todayIndex + 7) % 7;
          const eventDate = new Date(today);
          eventDate.setDate(today.getDate() + diff);
          // Parse time
          const [time, modifier] = item.ClassTime.split(' ');
          let [hours, minutes] = time.split(':').map(Number);
          if (modifier === 'PM' && hours < 12) hours += 12;
          if (modifier === 'AM' && hours === 12) hours = 0;
          eventDate.setHours(hours, minutes);
          return {
            title: item.CourseName,
            start: eventDate.toISOString(),
            end: new Date(eventDate.getTime() + 60 * 60 * 1000).toISOString(), // 1 hour duration
            url: item.ClassLink,
            extendedProps: {
              teacherName: item.TeacherName,
              courseName: item.CourseName,
            },
          };
        });
        setEvents(transformedEvents);
      } catch (error) {
        // error
      }
    };
    fetchData();
  }, [isStudent, isTeacher, userInfo]);

  const determineUserRole = (usertype) => {
    if (usertype === "Student") return "Student";
    else if (usertype === "Teacher") return "Teacher";
    else if (usertype === "Admin") return "Admin";
    else return "guest";
  };

  const [currentuserRole, setCurrentUserRole] = useState("");
  const navigate = useNavigate();
  const onLogin = (usertype) => {
    const userRole = determineUserRole(usertype);
    setUserRole(userRole);
    setCurrentUserRole(userRole);
  };

  async function onLogout() {
    await fetch(`${(process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_LOCAL_SERVER_API)}/logout`, {
      credentials: "include",
      method: "POST",
    });
    setUserInfo(null);
    setUserRole("guest");
    navigate("/");
  }

  const username = userInfo?.username;
  const _avatar_ = userInfo?.avatar;

  if (loading) return null;

  const renderEventContent = (eventInfo) => {
    return (
      <div>
        <b>{eventInfo.timeText}</b>
        <div data-tip data-for={eventInfo.event.id}>
          {eventInfo.event.title}
        </div>
        <Tooltip id={eventInfo.event.id} place="top" effect="solid">
          <div>Teacher: {eventInfo.event.extendedProps.teacherName}</div>
          <div>Course: {eventInfo.event.extendedProps.courseName}</div>
        </Tooltip>
      </div>
    );
  };

  const getMonthName = (date) => {
    return date.toLocaleString('default', { month: 'long' });
  };

  const handlePrevMonth = () => {
    setCalendarDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  };
  const handleNextMonth = () => {
    setCalendarDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  };

  return (
    <>
      {/* {showMobileLabel && (
        <div className="calendar-label-mobile">Calendar</div>
      )} */}
      {isAdmin && username && (
        <AdminNav username={username} avatar={_avatar_} onLogout={onLogout} />
      )}
      {isTeacher && username && (
        <TeacherNav username={username} avatar={_avatar_} onLogout={onLogout} />
      )}
      {isStudent && username && (
        <StudentNav username={username} avatar={_avatar_} onLogout={onLogout} />
      )}
      <div className="calendar-container-wrapper">
      <div className="calendar-container">
      <h4 className="calendar-heading2">Calendar</h4>
        <div className="calendar-month-header">
          <button className="calendar-arrow calendar-arrow-left" onClick={handlePrevMonth}><FaChevronLeft /></button>
          <span className="calendar-month-name">{getMonthName(calendarDate)}</span>
          <button className="calendar-arrow calendar-arrow-right" onClick={handleNextMonth}><FaChevronRight /></button>
        </div>
        <div className="calendar-weekdays-row">
          <div className="calendar-weekday">SUN</div>
          <div className="calendar-weekday">MON</div>
          <div className="calendar-weekday">TUE</div>
          <div className="calendar-weekday">WED</div>
          <div className="calendar-weekday">THU</div>
          <div className="calendar-weekday">FRI</div>
          <div className="calendar-weekday">SAT</div>
        </div>
        <div className="calendar-grid">
          {(() => {
            const year = calendarDate.getFullYear();
            const month = calendarDate.getMonth();
            const firstDayOfMonth = new Date(year, month, 1);
            const firstDayOfWeek = firstDayOfMonth.getDay();
            const startDate = new Date(firstDayOfMonth);
            startDate.setDate(startDate.getDate() - firstDayOfWeek);
            const grid = [];
            for (let week = 0; week < 6; week++) {
              const row = [];
              for (let day = 0; day < 7; day++) {
                const cellDate = new Date(startDate);
                cellDate.setDate(startDate.getDate() + (week * 7) + day);
                const displayNum = cellDate.getDate();
                const isCurrentMonth = cellDate.getMonth() === month;
                const cellDateStr = `${cellDate.getFullYear()}-${String(cellDate.getMonth() + 1).padStart(2, '0')}-${String(cellDate.getDate()).padStart(2, '0')}`;
                const cellEvents = events.filter(ev => {
                  if (!ev.start) return false;
                  const eventDate = new Date(ev.start);
                  return eventDate.getFullYear() === cellDate.getFullYear() &&
                         eventDate.getMonth() === cellDate.getMonth() &&
                         eventDate.getDate() === cellDate.getDate();
                });
                row.push(
                  <div
                    key={`date-${cellDateStr}`}
                    className={`calendar-date-cell${isCurrentMonth ? '' : ' other-month'}${cellEvents.length > 0 ? ' has-event' : ''}`}
                  >
                    {displayNum}
                    {cellEvents.map((ev, idx) => (
                      <div 
                        className={`calendar-event-block event-color-${(idx % 3) + 1}`} 
                        key={`${ev.title}-${idx}`}
                      >
                        <div className="calendar-event-title">{ev.title}</div>
                        <div className="calendar-event-time">
                          {new Date(ev.start).toLocaleTimeString([], { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              }
              grid.push(
                <div className="calendar-grid-row" key={`row-${week}`}>{row}</div>
              );
            }
            return grid;
          })()}
        </div>
      </div>
      </div>
      {!isTeacher && !isStudent && <Footer />}
    </>
  );
}

export default MyCalendar;